import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Star, TrendingUp, Calendar, Flame, Trophy, BookOpen, Palette } from 'lucide-react';
import { ChildThemePicker } from '../../components/ChildThemePicker';

interface ChildContextType {
  selectedChild: string;
  selectedChildData: any;
  soundEnabled: boolean;
  theme: any;
}

export function ChildProfilePage() {
  const { selectedChild, selectedChildData, theme } = useOutletContext<ChildContextType>();
  const [showThemePicker, setShowThemePicker] = useState(false);

  const level = Math.floor(selectedChildData.total_points / 100) + 1;
  const progressToNextLevel = selectedChildData.total_points % 100;
  const daysLearning = Math.floor((Date.now() - new Date(selectedChildData.created_at).getTime()) / (1000 * 60 * 60 * 24));

  const isDarkMode = theme.mode === 'dark' || theme.mode === 'high-contrast';

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4 text-[var(--text-primary)] font-baloo">
          Your Learning Profile
        </h1>
        <p className="text-xl text-[var(--text-secondary)]">
          See how amazing you are, {selectedChildData.name}!
        </p>
      </div>

      {/* Profile Card */}
      <div className={`rounded-3xl p-8 text-white shadow-2xl ${
        theme.mode === 'calm' 
          ? 'bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-600'
          : theme.mode === 'warm'
          ? 'bg-gradient-to-r from-orange-400 via-amber-500 to-yellow-600'
          : theme.mode === 'high-contrast'
          ? 'bg-gradient-to-r from-blue-600 to-purple-800 border-4 border-white'
          : isDarkMode 
          ? 'bg-gradient-to-r from-gray-800 via-blue-800 to-purple-800' 
          : 'bg-gradient-to-r from-blue-500 via-purple-600 to-indigo-700'
      }`}>
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-6">
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center text-white font-bold text-3xl border-4 border-white border-opacity-30 shadow-lg"
              style={{ backgroundColor: selectedChildData.avatar_color }}
            >
              {selectedChildData.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-2 font-baloo">{selectedChildData.name}</h2>
              <p className="text-xl opacity-90">
                Age {selectedChildData.age} • Learning Superstar!
              </p>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-5xl font-bold font-baloo">{selectedChildData.total_points}</div>
            <div className="text-lg opacity-90">
              Total Points
            </div>
          </div>
        </div>

        {/* Level Progress */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <span className="text-xl font-bold font-baloo">Level {level}</span>
            <span className="text-lg opacity-90">
              {progressToNextLevel}/100 to Level {level + 1}
            </span>
          </div>
          <div className="w-full bg-white bg-opacity-20 rounded-full h-4">
            <div
              className={`bg-white h-4 rounded-full shadow-sm ${
                theme.reducedMotion ? '' : 'transition-all duration-500'
              }`}
              style={{ width: `${progressToNextLevel}%` }}
            />
          </div>
          <div className="text-center mt-2 opacity-90">
            {100 - progressToNextLevel} more points to level up!
          </div>
        </div>
      </div>

      {/* Customize Theme Button */}
      <div className="flex justify-center">
        <button
          onClick={() => setShowThemePicker(true)}
          className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-xl hover:opacity-90 font-semibold transition-opacity text-lg shadow-md font-baloo"
        >
          <Palette className="w-5 h-5" />
          <span>Customize My Theme</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`rounded-3xl p-6 shadow-xl border-4 border-yellow-200 text-center ${
          theme.mode === 'high-contrast'
            ? 'bg-yellow-500 text-black'
            : 'bg-[var(--bg-secondary)]'
        }`}>
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Star className="w-8 h-8 text-yellow-600" />
          </div>
          <div className={`text-3xl font-bold ${
            theme.mode === 'high-contrast' ? 'text-black' : 'text-[var(--text-primary)]'
          } font-baloo`}>
            {selectedChildData.total_points}
          </div>
          <div className={`text-lg ${
            theme.mode === 'high-contrast' ? 'text-black' : 'text-[var(--text-secondary)]'
          }`}>
            Total Points Earned
          </div>
        </div>

        <div className={`rounded-3xl p-6 shadow-xl border-4 border-green-200 text-center ${
          theme.mode === 'high-contrast'
            ? 'bg-green-500 text-black'
            : 'bg-[var(--bg-secondary)]'
        }`}>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <TrendingUp className="w-8 h-8 text-green-600" />
          </div>
          <div className={`text-3xl font-bold ${
            theme.mode === 'high-contrast' ? 'text-black' : 'text-[var(--text-primary)]'
          } font-baloo`}>
            {level}
          </div>
          <div className={`text-lg ${
            theme.mode === 'high-contrast' ? 'text-black' : 'text-[var(--text-secondary)]'
          }`}>
            Current Level
          </div>
        </div>

        <div className={`rounded-3xl p-6 shadow-xl border-4 border-blue-200 text-center ${
          theme.mode === 'high-contrast'
            ? 'bg-blue-500 text-white'
            : 'bg-[var(--bg-secondary)]'
        }`}>
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-blue-600" />
          </div>
          <div className={`text-3xl font-bold ${
            theme.mode === 'high-contrast' ? 'text-white' : 'text-[var(--text-primary)]'
          } font-baloo`}>
            {daysLearning}
          </div>
          <div className={`text-lg ${
            theme.mode === 'high-contrast' ? 'text-white' : 'text-[var(--text-secondary)]'
          }`}>
            Days Learning
          </div>
        </div>
      </div>

      {/* Fun Facts */}
      <div className={`rounded-3xl shadow-xl border-4 border-purple-200 p-8 ${
        theme.mode === 'high-contrast'
          ? 'bg-purple-500 text-white'
          : 'bg-[var(--bg-secondary)]'
      }`}>
        <h3 className={`text-3xl font-bold text-center mb-8 ${
          theme.mode === 'high-contrast' ? 'text-white' : 'text-[var(--text-primary)]'
        } font-baloo`}>
          Fun Facts About You!
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-10 h-10 text-purple-600" />
            </div>
            <h4 className={`text-xl font-bold mb-2 ${
              theme.mode === 'high-contrast' ? 'text-white' : 'text-[var(--text-primary)]'
            } font-baloo`}>
              Learning Champion
            </h4>
            <p className={`text-lg ${
              theme.mode === 'high-contrast' ? 'text-white' : 'text-[var(--text-secondary)]'
            }`}>
              You've been learning for {daysLearning} days! That's amazing dedication!
            </p>
          </div>

          <div className="text-center">
            <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-10 h-10 text-pink-600" />
            </div>
            <h4 className={`text-xl font-bold mb-2 ${
              theme.mode === 'high-contrast' ? 'text-white' : 'text-[var(--text-primary)]'
            } font-baloo`}>
              Knowledge Collector
            </h4>
            <p className={`text-lg ${
              theme.mode === 'high-contrast' ? 'text-white' : 'text-[var(--text-secondary)]'
            }`}>
              Every activity you complete makes you smarter and stronger!
            </p>
          </div>

          <div className="text-center">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Flame className="w-10 h-10 text-orange-600" />
            </div>
            <h4 className={`text-xl font-bold mb-2 ${
              theme.mode === 'high-contrast' ? 'text-white' : 'text-[var(--text-primary)]'
            } font-baloo`}>
              Streak Master
            </h4>
            <p className={`text-lg ${
              theme.mode === 'high-contrast' ? 'text-white' : 'text-[var(--text-secondary)]'
            }`}>
              Keep learning every day to build your learning streak!
            </p>
          </div>

          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-10 h-10 text-green-600" />
            </div>
            <h4 className={`text-xl font-bold mb-2 ${
              theme.mode === 'high-contrast' ? 'text-white' : 'text-[var(--text-primary)]'
            } font-baloo`}>
              Point Collector
            </h4>
            <p className={`text-lg ${
              theme.mode === 'high-contrast' ? 'text-white' : 'text-[var(--text-secondary)]'
            }`}>
              You're doing great! Keep earning points to unlock new levels!
            </p>
          </div>
        </div>
      </div>

      {/* Encouragement Message */}
      <div className={`rounded-3xl p-8 text-white text-center shadow-2xl ${
        theme.mode === 'calm' 
          ? 'bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-600'
          : theme.mode === 'warm'
          ? 'bg-gradient-to-r from-orange-400 via-amber-500 to-yellow-600'
          : theme.mode === 'high-contrast'
          ? 'bg-gradient-to-r from-blue-600 to-purple-800 border-4 border-white'
          : isDarkMode 
          ? 'bg-gradient-to-r from-purple-800 via-blue-800 to-indigo-800' 
          : 'bg-gradient-to-r from-pink-400 via-purple-500 to-indigo-500'
      }`}>
        <h3 className="text-3xl font-bold mb-4 font-baloo">You're Doing Amazing!</h3>
        <p className="text-xl leading-relaxed opacity-90">
          Every day you learn something new, you become more awesome! 
          Keep exploring, keep asking questions, and keep being the amazing learner you are! 🌟
        </p>
      </div>

      {/* Theme Picker Modal */}
      {showThemePicker && (
        <ChildThemePicker
          isOpen={showThemePicker}
          onClose={() => setShowThemePicker(false)}
          childId={selectedChild}
          childName={selectedChildData.name}
        />
      )}
    </div>
  );
}